import { useState } from 'react';
import { Bell, Shield, Database, Save, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function SettingsPage() {
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const [notifications, setNotifications] = useState({
    shared: true,
    sla: true,
    weekly: false,
  });
  const [ragConfig, setRagConfig] = useState({
    reranking: true,
    queryRewriting: true,
    collection: 'apoyo_financiero',
  });

  function handleSave() {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuración</h1>
        <p className="text-sm text-gray-500 mt-1">Administra tu perfil y preferencias del agente.</p>
      </div>

      {/* Perfil */}
      <div className="bg-white rounded-xl border border-gray-100 p-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-3 mb-5">
          <Shield className="w-5 h-5" style={{ color: '#EDAA00' }} />
          <h2 className="text-sm font-semibold text-gray-900">Perfil de usuario</h2>
        </div>
        <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50">
          <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold text-white" style={{ backgroundColor: '#1B2A4A' }}>
            {user?.avatar}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{user?.name}</p>
            <p className="text-sm text-gray-500">{user?.email}</p>
            <p className="text-xs text-gray-400 mt-0.5">{user?.role} - {user?.department}</p>
          </div>
          <span className="ml-auto px-3 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: '#FFF3D0', color: '#C48E00' }}>SSO Activo</span>
        </div>
      </div>

      {/* Notificaciones */}
      <div className="bg-white rounded-xl border border-gray-100 p-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-3 mb-5">
          <Bell className="w-5 h-5" style={{ color: '#EDAA00' }} />
          <h2 className="text-sm font-semibold text-gray-900">Notificaciones</h2>
        </div>
        <div className="space-y-4">
          {[
            { key: 'shared' as const, label: 'Análisis compartidos', desc: 'Recibir notificación cuando alguien comparta un análisis contigo.' },
            { key: 'sla' as const, label: 'Alertas de ANS', desc: 'Notificar cuando se detecten patrones de incumplimiento de SLA.' },
            { key: 'weekly' as const, label: 'Resumen semanal', desc: 'Recibir un resumen semanal de actividad por correo.' },
          ].map(item => (
            <label key={item.key} className="flex items-start justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
              <div>
                <p className="text-sm font-medium text-gray-900">{item.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
              </div>
              <div className="relative inline-flex items-center mt-0.5">
                <input
                  type="checkbox"
                  checked={notifications[item.key]}
                  onChange={() => setNotifications(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                  className="sr-only peer"
                />
                <div className={`w-10 h-5.5 rounded-full transition-colors cursor-pointer ${notifications[item.key] ? '' : 'bg-gray-300'}`}
                  style={notifications[item.key] ? { backgroundColor: '#EDAA00' } : {}}
                  onClick={() => setNotifications(prev => ({ ...prev, [item.key]: !prev[item.key] }))}>
                  <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform mt-[3px] ${notifications[item.key] ? 'translate-x-[22px]' : 'translate-x-[3px]'}`} />
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Config RAG */}
      <div className="bg-white rounded-xl border border-gray-100 p-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-3 mb-5">
          <Database className="w-5 h-5" style={{ color: '#EDAA00' }} />
          <h2 className="text-sm font-semibold text-gray-900">Configuración del Agente</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Colección de datos</label>
            <input
              value={ragConfig.collection}
              onChange={e => setRagConfig(prev => ({ ...prev, collection: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
            />
          </div>
          {[
            { key: 'reranking' as const, label: 'Reranking de documentos', desc: 'Reordenar documentos recuperados por relevancia.' },
            { key: 'queryRewriting' as const, label: 'Reescritura de consultas', desc: 'Mejorar las consultas automáticamente con LLM.' },
          ].map(item => (
            <label key={item.key} className="flex items-start justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
              <div>
                <p className="text-sm font-medium text-gray-900">{item.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
              </div>
              <div className={`w-10 h-5.5 rounded-full transition-colors cursor-pointer ${ragConfig[item.key] ? '' : 'bg-gray-300'}`}
                style={ragConfig[item.key] ? { backgroundColor: '#EDAA00' } : {}}
                onClick={() => setRagConfig(prev => ({ ...prev, [item.key]: !prev[item.key] }))}>
                <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform mt-[3px] ${ragConfig[item.key] ? 'translate-x-[22px]' : 'translate-x-[3px]'}`} />
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all"
          style={{ backgroundColor: saved ? '#10B981' : '#1B2A4A' }}
        >
          {saved ? <><Check className="w-4 h-4" /> Guardado</> : <><Save className="w-4 h-4" /> Guardar cambios</>}
        </button>
      </div>
    </div>
  );
}
